/** 학생 v2의 입력과 작업 버튼. 공용 Button과 기존 v2 표의 작은 행 작업을 기준으로 한다. */
import React, { forwardRef } from 'react';
import {
  ArrowDown, ArrowLeft, ArrowRightLeft, ArrowUp, CalendarDays, ClipboardCheck,
  Eye, EyeOff, Loader2, LogOut, Pencil, Plus, Power, RotateCcw, Save, Trash2, UserPlus, X,
} from 'lucide-react';
import { Button } from '../common/Button';
import { DateTimeInput } from './DateTimeInput';
import './controls.css';

const ICONS = {
  '수정': Pencil, '이름 수정': Pencil, '정보 수정': Pencil, '정정': Pencil,
  '감추기': EyeOff, '되살리기': RotateCcw, '지우기': Trash2, '삭제': Trash2, '빼기': Trash2,
  '저장': Save, '수정 저장': Save, '취소': X, '닫기': X, '등록': UserPlus,
  '반 만들기': Plus, '부서 추가': Plus, '학교 추가': Plus, '추가': Plus, '수업 칸': Plus,
  '보강·추가 회차': Plus, '보강·추가 회차로 입력': Plus,
  '담임 교체': ArrowRightLeft, '시간표 변경': CalendarDays, '학생 입반': UserPlus,
  '출결 입력': ClipboardCheck, '학교·학년 변경': Pencil, '부서 변경': ArrowRightLeft,
  '입반': UserPlus, '입반(반 추가)': UserPlus, '반 이동': ArrowRightLeft, '퇴반': LogOut,
  '재등록': UserPlus, '휴원': Power, '메모 쓰기': Pencil, '퇴원 예정 취소': RotateCcw,
  '비활성화': Power, '다시 활성화': Power, '복귀': RotateCcw, '퇴원': LogOut,
  '위로': ArrowUp, '아래로': ArrowDown, '보기': Eye, '가리기': EyeOff,
  '내 반 목록': ArrowLeft, '반 목록': ArrowLeft, '내 반 현황': ArrowLeft, '학원 현황': ArrowLeft,
};

export const actionIcon = (label) => {
  const text = String(label || '').replace(/^\+\s*/, '');
  return ICONS[text] || (/정정|정보 수정|퇴원일 변경/.test(text) ? Pencil : null);
};

/** 모달·머리의 실행 버튼 — 기본은 작은 초록 테두리, 저장·취소·삭제는 공용 variant를 지정한다. */
export function AcademyButton({ children, icon: Icon, variant = 'primary-outline', size = 'xs', className = '', ...props }) {
  const Glyph = Icon || (typeof children === 'string' ? actionIcon(children) : null);
  return (
    <Button {...props} variant={variant} size={size} rounded="3xl"
      icon={!props.loading && Glyph ? <Glyph aria-hidden="true" className="size-3.5" /> : null}
      className={`shrink-0 ${size === 'xs' ? 'min-h-8' : 'min-h-10'} ${className}`}>
      {children}
    </Button>
  );
}

/** 표 안의 작은 작업 — 시험지 v2 RowAction과 같은 크기·색·호버 표시. */
export const ActionButton = forwardRef(function ActionButton({ children, icon: Icon, iconOnly = false, danger = false, prominent = false, loading = false, className = '', ...props }, ref) {
  const label = iconOnly && typeof children === 'string' ? children : undefined;
  const Glyph = loading ? Loader2 : Icon || (typeof children === 'string' ? actionIcon(children) : null);
  const tone = prominent ? 'bg-primary text-white hover:bg-primary-hover'
    : danger ? 'text-danger hover:bg-red-50' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900';
  return (
    <button {...props} ref={ref} type="button" disabled={props.disabled || loading}
      aria-label={props['aria-label'] || label}
      title={props.title || label}
      aria-busy={loading || undefined}
      className={`inline-flex min-h-7 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-3xl px-2 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-40 ${tone} ${className}`}>
      {Glyph && <Glyph aria-hidden="true" className={`size-3.5 shrink-0 ${loading ? 'animate-spin' : ''}`} />}
      {!iconOnly && children}
    </button>
  );
});

export const saveIcon = Save;
export const checkboxClass = 'size-4 shrink-0 rounded border-gray-300 accent-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1';

const inputSurface = 'academy-input min-w-0 rounded-lg border border-gray-300 bg-white text-gray-900 transition-colors placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400';
export const inputBase = `${inputSurface} h-10 px-3 py-2 text-sm`;
export const compactInputBase = `${inputSurface} h-8 px-2.5 py-1 text-xs`;
export const inputCls = `w-full ${inputBase}`;

const inputClass = (compact, className, fullWidth) => `${fullWidth ? 'w-full' : ''} ${compact ? compactInputBase : inputBase} ${className || ''}`;

/** 날짜·시간의 저장 형식과 범위는 그대로 두고 공통 팝업으로 선택한다. */
export function DateInput({ value, onChange, compact = false, fullWidth = true, className = '', ...props }) {
  return <DateTimeInput {...props} kind="date" value={value} onChange={onChange} className={inputClass(compact, className, fullWidth)} />;
}

export function TimeInput({ value, onChange, compact = false, fullWidth = true, className = '', ...props }) {
  return <DateTimeInput {...props} kind="time" value={value} onChange={onChange} className={inputClass(compact, className, fullWidth)} />;
}

export function SelectInput({ value, onChange, options, placeholder, compact = false, fullWidth = true, className = '', ...props }) {
  return (
    <select {...props} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={inputClass(compact, className, fullWidth)}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
    </select>
  );
}

/** 공통 드롭다운으로 하나씩 추가하고, 선택한 항목은 아래에서 개별 해제한다. 값의 원래 형식을 유지한다. */
export function MultiSelectInput({ value = [], onChange, options, placeholder = '선택', emptyText = '선택할 항목이 없습니다.', disabled = false, ...props }) {
  const selected = new Set(value.map(String));
  const available = options.filter(([id]) => !selected.has(String(id)));
  return (
    <div className="min-w-0 space-y-2">
      <SelectInput
        {...props}
        value=""
        options={available}
        placeholder={!options.length ? emptyText : !available.length ? '모두 선택했습니다.' : placeholder}
        disabled={disabled || !available.length}
        onChange={(id) => {
          const option = available.find(([key]) => String(key) === id);
          if (option) onChange([...value, option[0]]);
        }}
      />
      {value.length > 0 && (
        <ul aria-label="선택한 항목" className="flex flex-wrap gap-2">
          {value.map((id) => {
            const label = options.find(([key]) => String(key) === String(id))?.[1] ?? String(id);
            return (
              <li key={id} className="inline-flex max-w-full items-center gap-1 rounded-lg bg-gray-100 py-1 pl-2.5 pr-1 text-xs text-gray-800">
                <span className="min-w-0 break-all">{label}</span>
                <ActionButton
                  icon={X}
                  iconOnly
                  disabled={disabled}
                  aria-label={`${label} 선택 해제`}
                  onClick={() => onChange(value.filter((key) => String(key) !== String(id)))}
                >선택 해제</ActionButton>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function TextInput({ value, onChange, compact = false, fullWidth = true, className = '', ...props }) {
  return <input {...props} type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} className={inputClass(compact, className, fullWidth)} />;
}

export function TextArea({ value, onChange, rows = 3, className = '', ...props }) {
  return <textarea {...props} value={value || ''} onChange={(e) => onChange(e.target.value)} rows={rows} className={`w-full ${inputSurface} px-3 py-2 text-sm ${className}`} />;
}
